import './commentForm.scss';


import React, { useCallback, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { ServicesContext } from "@sbi-international/boutik.gatsby.plugin.services/dist/src/contexts/servicesContext";
import { getBase64 } from '../../../utils/getBase64';
import { FeedbackContext } from '../../../contexts/feedback/feedbackContext';
import useMobileDetect from '../../../hooks/useMobileDetect';
import { IProductReviewRatingMetadata } from '@sbi-international/boutik.gatsby.plugin.services';
import { Button, Input } from '../../primitives';

type CommentFormProps = {
    name: string;
    setName: React.Dispatch<React.SetStateAction<string>>;
    title: string;
    setTitle: React.Dispatch<React.SetStateAction<string>>;
    comment: string;
    setComment: React.Dispatch<React.SetStateAction<string>>;
    sku: string;
    slug: any;
    setWriteCommentOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function CommentForm(props: CommentFormProps) {
    const {
        name,
        setName,
        title,
        setTitle,
        comment,
        setComment,
        sku,
        slug,
        setWriteCommentOpen,
    } = props;

    const { currentLocale, navigateTranslated } = usePageLocalization();
    const [reviewsMetadata, setReviewsMetadata] = useState<IProductReviewRatingMetadata>();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const { t } = useTranslation('templatePages');
    const { addAlert } = useContext<any>(FeedbackContext);
    const [newRating, setNewRating] = useState(0);
    const [reviewAttachments, setReviewAttachments]: [
        string | undefined,
        React.Dispatch<React.SetStateAction<any>>
    ] = useState(undefined);
    const [errors, setErrors] = useState({
        name: '',
        title: '',
        comment: '',
        starRating: '',
    });
    const isMobile = useMobileDetect().isMobile();
    
    const servicesContext = useContext(ServicesContext);
    const productReviewsService = servicesContext?.magento.productReviewsService;

    const isFormValid = useCallback(() => {
        let isValid = true;

        for (const value of Object.values(errors)) {
            if (value.trim() !== '') {
                isValid = false;
                break;
            }
        }

        if (name === '') {
            setErrors((errors) => ({ ...errors, name: ' ' }));
            isValid = false;
        }

        if (title === '') {
            setErrors((errors) => ({ ...errors, title: ' ' }));
            isValid = false;
        }

        if (comment === '') {
            setErrors((errors) => ({ ...errors, comment: 'error' }));
            isValid = false;
        }

        if (newRating === 0) {
            setErrors((errors) => ({ ...errors, starRating: 'error' }));
            isValid = false;
        }

        return isValid;
    }, [errors, comment, title, newRating, name]);

    const removeFirstPathname = (slug: Record<string, string>) => {
        let newSlug: any = {};
        for (const [key, value] of Object.entries(slug)) {
            newSlug[key] = value?.split('/')?.slice(2)?.join('/') + '/';
        }
        return newSlug;
    };

    useEffect(() => {
        productReviewsService.getReviewRatingsOverallMetadata().then((data) => {
            setReviewsMetadata(data);
        });
    }, [productReviewsService]);

    const onSubmit = useCallback(
        async (event: any) => {
            event.preventDefault();

            if (isFormValid()) {
                productReviewsService.createReview({
                    sku: sku,
                    nickname: name,
                    text: comment,
                    file: selectedFile ?? undefined,
                    rating: newRating as 2 | 1 | 3 | 4 | 5,
                })
                    .then(async (data: any) => {
                        setWriteCommentOpen(false);
                        navigateTranslated(
                            `${removeFirstPathname(slug)[currentLocale]}`
                        );
                        addAlert({
                            message: t('toastMessages:review-message'),
                            status: 'success',
                        });
                    })
                    .catch((error) => {
                        addAlert({ message: error, status: 'error' });
                        console.error(error.message);
                    });
            }
            
        },
        [
            isFormValid,
            
            sku,
            name,
            title,
            comment,
            reviewsMetadata?.id,
            reviewsMetadata?.values,
            newRating,
            setWriteCommentOpen,
            navigateTranslated,
            slug,
            currentLocale,
            addAlert,
            t,
        ]
    );

    const uploadPhoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const currentFiles = event?.target?.files;
        if (currentFiles && currentFiles.length > 0) {
            setSelectedFile(currentFiles[0]); // Stocke le fichier brut
            getBase64(currentFiles[0]).then((base64_) => {
                setReviewAttachments(base64_); // Stocke l'aperçu base64
            });
        }
    };

    const attachPhoto = () => {
        const fileInput = document.getElementById('review-fileinput');

        if (fileInput) {
            fileInput.click();
        } else return undefined;
    };

    return (
        <form className='reviewForm' onSubmit={(event) => onSubmit(event)}>
            <div className='review-form-box'>
                <div className='inputGroup'>
                    <h3>{t('add-name-title')}</h3>
                    <Input
                        id={''}
                        validateInput={() => {
                            if (name === '')
                                setErrors({
                                    ...errors,
                                    name: t('field-required'),
                                });
                            else
                                setErrors({
                                    ...errors,
                                    name: '',
                                });
                        }}
                        autocomplete={''}
                        className='review-input'
                        type='text'
                        error={errors.name}
                        value={name}
                        onChange={(value: string) => {
                            setErrors((errors) => ({ ...errors, name: '' }));
                            setName(value);
                        }}
                        placeholder={t('add-name-placeholder') ?? ''}
                    />
                    <h3 className='title'>{t('add-review-title')}</h3>
                    <Input
                        id={''}
                        validateInput={() => {
                            if (title === '')
                                setErrors({
                                    ...errors,
                                    title: t('field-required'),
                                });
                            else
                                setErrors({
                                    ...errors,
                                    title: '',
                                });
                        }}
                        autocomplete={''}
                        type='text'
                        className='review-input'
                        error={errors.title}
                        value={title}
                        onChange={(value: string) => {
                            setErrors((errors) => ({ ...errors, title: '' }));
                            setTitle(value);
                        }}
                        placeholder={t('review-title-placeholder') ?? ''}
                    />
                    <h3 className='title'>{t('add-rating')}</h3>
                    <div
                        className={
                            errors.starRating === 'error'
                                ? 'review-starRating error'
                                : 'review-starRating'
                        }
                    >
                        {[1, 2, 3, 4, 5].map((index) => (
                            <span
                                className={classNames(`review-star ${index == 1 ? 'first' : ''}`, {
                                    selected: newRating >= index,
                                })}
                                key={`star-${index} `}
                                onClick={() => {
                                    setNewRating(index);
                                    setErrors((errors) => ({
                                        ...errors,
                                        starRating: '',
                                    }));
                                }}
                            />
                        ))}
                    </div>
                </div>

                <div className='inputGroup'>
                    <h3>{t('write-review')}</h3>
                    <textarea
                        className={errors.comment ? 'comment error' : 'comment'}
                        onChange={(event) => {
                            setComment(event.currentTarget.value);
                            setErrors((errors) => ({ ...errors, comment: '' }));
                        }}
                        placeholder={t('review-placeholder') ?? ''}
                    />
                </div>
                <input
                    style={{ display: 'none' }}
                    type='file'
                    accept='.jpg, .jpeg, .png'
                    onChange={(e) => {
                        uploadPhoto(e);
                    }}
                    id='review-fileinput'
                />
                <div className='btn-wrapper'>
                    <Button
                        type='submit'
                        className='send-review'
                        label={t('send') as string}
                    />
                </div>
            </div>
            <div className='add-picture-box'>
                <div onClick={attachPhoto} className='add-picture'>
                    <span>{t('add-image')}</span>
                </div>
                {reviewAttachments && (
                    <div
                        className={`attachments-div ${
                            isMobile ? 'mobile' : 'desktop'
                        }`}
                    >
                        <img
                            src={reviewAttachments}
                            alt=''
                            className='review-img-preview'
                        />

                        <span
                            onClick={() => {
                                setReviewAttachments(null);
                            }}
                            className='delete-img'
                        >
                            X
                        </span>
                    </div>
                )}
            </div>
        </form>
    );
}